/**
 * Sellable Type Select Component
 *
 * Building block for selecting sellable item type (plan, group, product, package).
 * Used by purchase triggers (any, some).
 *
 * Note: The sellable type is stored in `reminderType`, not a separate field.
 * When the trigger is initially 'some' or 'any', we show this dropdown to let
 * the user select the actual sellable type.
 */

import { useFormikContext } from 'formik';
import { FC } from 'react';
import { Box } from 'src/components/Box';
import { TextField } from 'src/components/TextField';
import { SellableType } from 'src/shared/Forms/Selects/SellableItemsMultiSelect';

import { SELLABLE_TYPE_OPTIONS } from '../automationTriggers';
import { AutomationItem } from '../types';

interface SellableTypeSelectProps {
  index: number;
  onChange?: (type: string) => void;
}

// Valid sellable types
const VALID_SELLABLE_TYPES = SELLABLE_TYPE_OPTIONS.map((opt) => opt.value);

export const SellableTypeSelect: FC<SellableTypeSelectProps> = ({
  index,
  onChange,
}) => {
  const { setFieldValue, values } = useFormikContext<{
    automations: AutomationItem[];
  }>();

  const automation = values.automations[index];
  const currentValue = automation?.reminderType || '';

  // If the current reminderType is not a valid sellable type (e.g., 'some', 'any'),
  // show empty so the placeholder displays
  const displayValue = VALID_SELLABLE_TYPES.includes(currentValue as SellableType)
    ? currentValue
    : '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value;
    setFieldValue(`automations.${index}.reminderType`, newType);
    // Reset selected items when type changes
    setFieldValue(`automations.${index}.reminderTypeId`, null);
    onChange?.(newType);
  };

  const options = [
    { value: '', label: 'Select type...' },
    ...SELLABLE_TYPE_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })),
  ];

  return (
    <Box sx={{ maxWidth: 300 }}>
      <TextField
        value={displayValue}
        onChange={handleChange}
        options={options}
        size="small"
      />
    </Box>
  );
};
